import config from '../config/appConfig';
import { apicFetch } from './apiTokenService';

/**
 * Fetch facets (filter options) for the current search
 * @param {Object} searchBody - Complete search body (same as provider search)
 * @returns {Promise<Object>} Facets response with filter options
 */
export const fetchFacets = async (searchBody) => {
  try {
    // Uses APIC_BASE_URL_API — NOT APIC_BASE_URL_API_V2. The facet search/retrieve
    // endpoint was originally documented as needing the extra /medicaid/qapath1 segment
    // (like the geolocation coordinates endpoint), but that turned out to be a typo from
    // the other team — facets actually follows the same plain base as the rest of the
    // stress endpoints. See appConfig.js's APIC_BASE_URL_API_V2 comment, now updated to
    // reflect that it's geolocation-only.
    const facetsUrl = `${config.APIC_BASE_URL_API}/hcb/v3/providersearch/providers/facet/search/retrieve`;
    
    console.log('');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔍 FACETS API REQUEST');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Method: POST');
    console.log('URL:', facetsUrl);
    console.log('POST Body:', JSON.stringify(searchBody, null, 2));
    console.log('═══════════════════════════════════════════════════════════');
    console.log('');
    
    const response = await apicFetch(facetsUrl, {
      method: 'POST',
      body: JSON.stringify(searchBody)
    });
    
    if (!response.ok) {
      console.log('');
      console.log('❌❌❌ FACETS API ERROR ❌❌❌');
      console.error('❌ FACETS API ERROR:', {
        timestamp: new Date().toISOString(),
        status: response.status,
        statusText: response.statusText,
        url: facetsUrl
      });
      console.log('');
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    console.log('');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('✅ FACETS API RESPONSE');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Status:', response.status, response.statusText);
    console.log('URL:', facetsUrl);
    console.log('Total Providers:', data.totalProviders);
    console.log('Facets Count:', data.facets?.length || 0);
    console.log('Facets:', data.facets?.map(f => f.key).join(', ') || 'None');
    console.log('Full Response:', data);
    console.log('═══════════════════════════════════════════════════════════');
    console.log('');
    
    return data;
  } catch (error) {
    console.error('❌ Error fetching facets:', error);
    throw error;
  }
};

/**
 * Parse facets response and extract filter options
 * @param {Object} facetsData - Response from facets API
 * @returns {Object} Parsed facets with normalized keys
 */
export const parseFacets = (facetsData) => {
  if (!facetsData || !facetsData.facets) {
    return {};
  }
  
  const parsedFacets = {};

  // ethnicity is the one facet whose legitimate option labels can themselves
  // contain commas (e.g. "Not of Hispanic, Latino/a, or Spanish Origin"), so it
  // must be excluded from the generic comma-splitting below (which exists for
  // facets like handicapAccessibility that really do pack multiple values into
  // a single comma-joined string) — otherwise that single ethnicity value gets
  // incorrectly shredded into multiple bogus filter options. All other facets
  // keep their original splitting behavior unchanged.
  const NO_COMMA_SPLIT_FACET_KEYS = new Set(['ethnicity']);

  facetsData.facets.forEach(facet => {
    // Deduplicate values by normalizing case
    // Keep the first occurrence (usually proper case) and ignore duplicates
    const seenValues = new Map(); // Use Map to preserve first occurrence
    const deduplicatedValues = [];
    const shouldSplitOnComma = !NO_COMMA_SPLIT_FACET_KEYS.has(facet.key);
    
    (facet.values || []).forEach(value => {
      // Some facets (like handicapAccessibility) return comma-separated values in a single string
      // Split by comma and trim each value — except for facet keys (like ethnicity)
      // whose values legitimately contain commas as part of a single label.
      const individualValues = shouldSplitOnComma && value.includes(',') 
        ? value.split(',').map(v => v.trim())
        : [value];
      
      individualValues.forEach(individualValue => {
        const normalizedKey = individualValue.toLowerCase();
        if (!seenValues.has(normalizedKey) && individualValue) { // Also check for empty strings
          seenValues.set(normalizedKey, true);
          deduplicatedValues.push(individualValue);
        }
      });
    });

    // Build a normalizedValue -> count lookup from the facet's own `options` array
    // (each { value, count } pair — the count of providers in the CURRENT result set
    // that have that value). Not every facet response includes `options` (older/mocked
    // responses only have `values`), so this is deliberately optional/best-effort —
    // callers that don't care about counts can keep using `values` exactly as before.
    const countsByValue = {};
    (facet.options || []).forEach(opt => {
      if (opt && typeof opt.value === 'string') {
        countsByValue[opt.value.toLowerCase()] = opt.count;
      }
    });
    
    parsedFacets[facet.key] = {
      label: facet.label,
      values: deduplicatedValues,
      // Same values as above, but paired with their result count when available — lets
      // filter-building code (see allProviderFilters.js's buildDynamicFilters) show real
      // counts next to each option and/or determine which of a filter's fixed set of
      // choices actually has matching data in the current results, without every caller
      // needing to separately re-derive this from facet.options.
      options: deduplicatedValues.map(value => ({
        value,
        count: countsByValue[value.toLowerCase()]
      }))
    };
  });
  
  console.log('📊 Parsed Facets:', {
    totalFacets: Object.keys(parsedFacets).length,
    facetKeys: Object.keys(parsedFacets),
    facetDetails: parsedFacets
  });
  
  return parsedFacets;
};

/**
 * Map facet keys to filter names used in the application
 */
export const FACET_TO_FILTER_MAP = {
  groupAffiliations: 'groupAffiliation',
  handicapAccessibility: 'handicapAccess',
  languagesSpoken: 'language',
  specializedTraining: 'specializedTraining',
  specialties: 'specialty',
  hospitalAffiliations: 'hospitalAffiliation',
  accreditation: 'accreditation',
  ethnicity: 'providerEthnicity',
  specialProviderType: 'specialProviderType'
};

/**
 * Get available filter options from facets for a specific filter
 * @param {Object} parsedFacets - Parsed facets object
 * @param {string} filterName - Filter name (e.g., 'language', 'specialty')
 * @returns {Array} Array of available options for the filter
 */
export const getFilterOptions = (parsedFacets, filterName) => {
  // Find the facet key that maps to this filter
  const facetKey = Object.keys(FACET_TO_FILTER_MAP).find(
    key => FACET_TO_FILTER_MAP[key] === filterName
  );
  
  if (!facetKey || !parsedFacets[facetKey]) {
    return [];
  }
  
  return parsedFacets[facetKey].values || [];
};
